import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { HardDrive, Warehouse, Link2, Unlink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader } from "@/components/Common";
import { DriveFolders } from "@/components/DriveFolders";
import { Button } from "@/components/ui/button";

const DriveCard = () => {
  const [st, setSt] = useState(null);
  const load = () => api.get("/drive/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const disconnect = async () => {
    await api.post("/drive/disconnect").then(load).catch((e) => toast.error(errMsg(e)));
  };

  const redirect = st?.redirect_uri || `${process.env.REACT_APP_BACKEND_URL}/api/drive/callback`;
  return (
    <div className="border bg-white p-6" data-testid="drive-card">
      <div className="flex items-start gap-4">
        <HardDrive className="h-6 w-6 text-primary" />
        <div className="flex-1">
          <h2 className="font-display text-xl font-bold">Google Drive aziendale</h2>
          <p className="mt-1 text-sm text-muted-foreground">Le foto e gli allegati delle chiusure vengono archiviati nella cartella «Archivio foto chiusure» del Drive, nella sottocartella dell'azienda che ha eseguito l'ordine e in una cartella per pratica. Se Drive non risponde, il file resta nell'ERP e viene spostato in automatico appena possibile.</p>
          <div className="mt-4 font-mono text-sm" data-testid="drive-status">
            Stato: {st?.connected ? <span className="text-emerald-700">Collegato {st.connected_by && `da ${st.connected_by}`} il {fmtDateTime(st.connected_at)}</span>
              : st?.configured ? <span className="text-amber-700">Pronto al collegamento</span> : <span className="text-red-600">Credenziali Google non configurate</span>}
          </div>
          {!st?.connected && (
            <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Google Cloud Console → crea un progetto e abilita "Google Drive API".</li>
              <li>Schermata consenso OAuth → tipo «Interno» se usi Google Workspace, altrimenti «Esterno» e poi «Pubblica app»; aggiungi lo scope …/auth/drive.</li>
              <li>Credenziali → ID client OAuth "Applicazione web" con URI di reindirizzamento: <code className="break-all text-foreground">{redirect}</code></li>
              <li>Comunica Client ID e Client Secret per completare la configurazione.</li>
            </ol>
          )}
          <div className="mt-5 flex gap-2">
            {st?.connected
              ? <Button variant="outline" onClick={disconnect} data-testid="drive-disconnect-button"><Unlink className="mr-1 h-4 w-4" />Scollega</Button>
              : <Button onClick={connect} disabled={!st?.configured} data-testid="drive-connect-button"><Link2 className="mr-1 h-4 w-4" />Collega Google Drive</Button>}
          </div>
          {st?.connected && <DriveFolders />}
        </div>
      </div>
    </div>
  );
};

export default function Settings() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("drive") === "ok") toast.success("Google Drive collegato");
    if (params.get("drive") === "errore") toast.error("Collegamento a Google Drive non riuscito");
    if (params.get("drive") === "cartella") toast.warning("Drive collegato, ma la cartella «Archivio foto chiusure» non è stata trovata: controlla il nome qui sotto");
  }, [params]);
  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <PageHeader eyebrow="Integration" title="Impostazioni" />
      <div className="space-y-4">
        <DriveCard />
        <div className="border bg-white p-6" data-testid="warehouse-card">
          <div className="flex items-start gap-4">
            <Warehouse className="h-6 w-6 text-muted-foreground" />
            <div>
              <h2 className="font-display text-xl font-bold">Magazzino — warehouse-ops-cloud</h2>
              <p className="mt-1 text-sm text-muted-foreground">Integrazione prevista nella fase successiva: articoli, seriali e consumi per ordine saranno sincronizzati con la piattaforma di magazzino esterna (URL e API da fornire).</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
